import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import AvatarImg from "./AvatarImg";
import AsyncActionButton from "./AsyncActionButton";
import { appConfirm } from "./AppDialog";
import { useLanguage } from "../i18n/LanguageContext";
import { deleteBirthdayWish, getBirthdayOccurrence, saveBirthdayWish } from "../services/birthdayService";
import "../styles/birthdayExperience.css";

const PRESETS = [
  { key: "happy_birthday", emoji: "🎂", tr: "İyi ki doğdun!", en: "Happy birthday!" },
  { key: "health", emoji: "🌿", tr: "Sağlıkla nice yaşlara", en: "Many healthy years" },
  { key: "joy", emoji: "✨", tr: "Neşen hep bol olsun", en: "May joy follow you" },
  { key: "dreams", emoji: "🌟", tr: "Tüm dileklerin gerçek olsun", en: "May your wishes come true" },
  { key: "love", emoji: "💜", tr: "Seni çok seviyoruz", en: "We love you so much" },
];

export default function BirthdayDetailSheet({ occurrenceId, onClose, onChanged }) {
  const { lang } = useLanguage();
  const en = lang === "en";
  const [occurrence, setOccurrence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("happy_birthday");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!occurrenceId) return;
    if (!silent) setLoading(true);
    setError("");
    if (!silent) setSent(false);
    try {
      const result = await getBirthdayOccurrence(occurrenceId);
      setOccurrence(result);
      const mine = (result?.wishes || []).find((wish) => wish.senderUid && wish.senderUid === result?.currentUserUid);
      if (mine) {
        setSelectedPreset(mine.presetKey || "happy_birthday");
        setMessage(mine.message || "");
        setSent(true);
      } else {
        setSelectedPreset("happy_birthday");
        setMessage("");
      }
    } catch (requestError) {
      setError(requestError?.message || (en ? "Birthday details could not be loaded." : "Doğum günü detayı yüklenemedi."));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [occurrenceId, en]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handleBirthdayChange = (event) => {
      const detail = event?.detail || {};
      if (
        detail.resource === "birthdays" &&
        (!detail.itemId || String(detail.itemId) === String(occurrenceId))
      ) {
        load({ silent: true });
      }
    };
    window.addEventListener("fp:dataChanged", handleBirthdayChange);
    return () => window.removeEventListener("fp:dataChanged", handleBirthdayChange);
  }, [occurrenceId, load]);

  const wishes = useMemo(() => Array.isArray(occurrence?.wishes) ? occurrence.wishes : [], [occurrence]);
  const firstName = String(occurrence?.celebrant?.name || "").trim().split(/\s+/)[0];

  const sendWish = async () => {
    if (!occurrenceId || sending || occurrence?.celebrant?.isCurrentUser) return;
    setSending(true);
    setError("");
    try {
      await saveBirthdayWish(occurrenceId, { presetKey: selectedPreset, message, update: sent });
      setSent(true);
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(requestError?.message || (en ? "Your wish could not be sent." : "Kutlaman gönderilemedi."));
    } finally {
      setSending(false);
    }
  };

  const removeWish = async () => {
    if (!sent || sending) return;
    const confirmed = await appConfirm(
      en ? "Remove your birthday wish?" : "Doğum günü kutlaman kaldırılsın mı?",
      { confirmText: en ? "Remove" : "Kaldır", cancelText: en ? "Cancel" : "Vazgeç" }
    );
    if (!confirmed) return;
    setSending(true);
    setError("");
    try {
      await deleteBirthdayWish(occurrenceId);
      setSent(false);
      setMessage("");
      setSelectedPreset("happy_birthday");
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(requestError?.message || (en ? "Your wish could not be removed." : "Kutlaman kaldırılamadı."));
    } finally {
      setSending(false);
    }
  };

  if (!occurrenceId) return null;
  const content = (
    <div className="modalOverlay birthdaySheetOverlay" onClick={onClose} role="presentation">
      <div className="birthdaySheet" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="birthdaySheetGlow" />
        <button type="button" className="birthdaySheetClose" onClick={onClose} aria-label={en ? "Close" : "Kapat"}>×</button>

        {loading ? (
          <div className="birthdaySheetState"><span className="birthdayLoader" />{en ? "Preparing the celebration…" : "Kutlama hazırlanıyor…"}</div>
        ) : error && !occurrence ? (
          <div className="birthdaySheetState birthdaySheetError">{error}<button onClick={load}>{en ? "Try again" : "Tekrar dene"}</button></div>
        ) : occurrence ? (
          <>
            <div className="birthdaySheetHero">
              <div className="birthdaySheetAvatar">
                <AvatarImg avatar={occurrence.celebrant?.avatar || null} initials={(firstName || "?").charAt(0)} size={76} radius={26} />
                <i>🎂</i>
              </div>
              <small>{en ? "BIRTHDAY CELEBRATION" : "DOĞUM GÜNÜ KUTLAMASI"}</small>
              <h2>{occurrence.celebrant?.isCurrentUser ? (en ? "Today is your day" : "Bugün senin günün") : `${firstName}${en ? "'s birthday" : "’in doğum günü"}`}</h2>
              <p>{occurrence.familyName}</p>
            </div>

            {!occurrence.celebrant?.isCurrentUser && occurrence.canWish && (
              <div className="birthdayComposer">
                <h3>{en ? "Choose your wish" : "Dileğini seç"}</h3>
                <div className="birthdayPresetRail">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      className={selectedPreset === preset.key ? "active" : ""}
                      onClick={() => setSelectedPreset(preset.key)}
                    >
                      <span>{preset.emoji}</span>{en ? preset.en : preset.tr}
                    </button>
                  ))}
                </div>
                <label className="birthdayMessageLabel">
                  <span>{en ? "Add a personal note (optional)" : "Kişisel bir not ekle (isteğe bağlı)"}</span>
                  <textarea
                    value={message}
                    maxLength={280}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder={en ? `Write something special for ${firstName}…` : `${firstName} için içinden geleni yaz…`}
                  />
                  <small>{message.length}/280</small>
                </label>
                {error && <div className="birthdayInlineError" role="alert">{error}</div>}
                <AsyncActionButton className={`birthdaySendButton ${sent ? "sent" : ""}`} externalBusy={sending} onAction={sendWish}>
                  {sent ? (en ? "Update my wish" : "Kutlamamı güncelle") : (en ? "Send birthday wish" : "Doğum gününü kutla")}
                  <span>{sent ? "✓" : "→"}</span>
                </AsyncActionButton>
                {sent && <button type="button" className="birthdayRemoveWish" disabled={sending} onClick={removeWish}>{en ? "Remove my wish" : "Kutlamamı kaldır"}</button>}
              </div>
            )}

            {!occurrence.celebrant?.isCurrentUser && !occurrence.canWish && Number(occurrence.daysUntil) > 0 && (
              <div className="birthdayCountdownCard">
                <span>🎁</span>
                <div>
                  <strong>{en ? `${occurrence.daysUntil} days to go` : `${occurrence.daysUntil} gün kaldı`}</strong>
                  <p>{en ? "Come back on the birthday to send your wish with one tap." : "Doğum günü geldiğinde tek dokunuşla kutlamak için yeniden gel."}</p>
                </div>
              </div>
            )}

            {wishes.length > 0 && (
              <div className="birthdayWishWall">
                <div className="birthdayWishWallHeader"><h3>{en ? "Birthday wishes" : "Doğum günü dilekleri"}</h3><span>{wishes.length}</span></div>
                {wishes.map((wish) => {
                  const preset = PRESETS.find((item) => item.key === wish.presetKey) || PRESETS[0];
                  return (
                    <div className="birthdayWishCard" key={wish.id}>
                      <AvatarImg avatar={wish.senderAvatar || null} initials={String(wish.senderName || "?").charAt(0)} size={40} radius={14} />
                      <div><strong>{wish.senderName}</strong><p>{wish.message || `${preset.emoji} ${en ? preset.en : preset.tr}`}</p></div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
